import {ErrorBoundary} from 'react-error-boundary';
import {Suspense} from 'react';
import {useSuspenseQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {
  getIsMidwayPerformanceRegistrationVisible,
  getIsRegistrationRequired,
} from '@di-shared/features/mileage/ecomileage/MidwayPerformanceRegistrationUtils';
import {WebviewUtils} from '@shared/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';

interface MidwayPerformanceRegistrationProps {
  vin: string;
}

/**
 * 25.01.01 이후 마일리지 가입자 대상으로 표출
 * 서울시 동의 API의 created_at'(최초 등록일)이 25.01.01. 이후
 * 제한 주행거리 업데이트 중 상태에만 표출
 *
 * middle_rgs_yn
 * 중간 주행거리 실적 등록 필요 여부
 * 01: 전송필요, 02: 불필요
 * (01 로 전송시 등록요청일자 -1 일의 최종 누적주행거리 전송)
 */

function MidwayPerformanceRegistration({vin}: MidwayPerformanceRegistrationProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<MidwayPerformanceRegistrationLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: MidwayPerformanceRegistrationProps) {
  const [{data: infoLinkData}, {data: agreeData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.ecoMileageController.getInfoLink), vin],
        queryFn: () =>
          diApi.ecoMileageController.getInfoLink({
            vin,
          }),
      },
      {
        queryKey: [getQueryKey(diApi.ecoMileageController.getAgree), vin],
        queryFn: () =>
          diApi.ecoMileageController.getAgree({
            vin,
          }),
      },
    ],
  });

  const isMidwayPerformanceRegistrationVisible = getIsMidwayPerformanceRegistrationVisible(agreeData.data.createdAt);
  const isRegistrationRequired = getIsRegistrationRequired(infoLinkData.data.rtn_data.middle_rgs_yn);

  if (!isMidwayPerformanceRegistrationVisible) {
    return <></>;
  }
  if (isRegistrationRequired) {
    return <MidwayPerformanceRegistrationOpen vin={vin} />;
  }
  return <MidwayPerformanceRegistrationPending registrationDateString={infoLinkData.data.rtn_data.middle_dstnc_rgsde} />;
}

export default MidwayPerformanceRegistration;

interface MidwayPerformanceRegistrationPendingProps {
  registrationDateString: string;
}

function MidwayPerformanceRegistrationPending({registrationDateString}: MidwayPerformanceRegistrationPendingProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const registrationDate = parseDateWithFormat(registrationDateString);
  return (
    <div className="section_container section_mileage_registration">
      <div className="section_container_header">
        <h4 className="body_xlarge_b">{t('T30395')}</h4>
      </div>
      <p className="section_container_desc">
        {t('T30397_$date', {date: registrationDate && CldrDatetimeFormatter.format(registrationDate, 'yMMM')})}
      </p>
      <p className="section_container_desc">{t('T30398')}</p>
    </div>
  );
}

interface MidwayPerformanceRegistrationOpenProps {
  vin: string;
}

function MidwayPerformanceRegistrationOpen({vin}: MidwayPerformanceRegistrationOpenProps) {
  const {t} = useInferableTranslation();

  function openRegisterPage() {
    WebviewUtils.openNewWebView({
      url: `${window.location.origin}${ROUTE_PATHS.MILEAGE_MID_REGISTER.getFullPath(vin)}`,
      isModal: true,
      isNavigationBar: false,
      isToolBar: false,
    });
  }

  return (
    <div className="section_container section_mileage_registration">
      <div className="section_container_header">
        <h4 className="body_xlarge_b">{t('T30395')}</h4>
      </div>
      <p
        className="section_container_desc"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {t('T30396')}
      </p>

      <button
        type="button"
        className="btn btn_large btn_filled btn_full"
        onClick={openRegisterPage}
      >
        {t('T30394')}
      </button>
    </div>
  );
}

function MidwayPerformanceRegistrationLoading() {
  return (
    <div
      className="section_container section_mileage_registration skeleton"
      style={{height: '190px'}}
    />
  );
}
